import { readEmailWorkingCopy, saveEmailWorkingCopy } from '../../lib/email-authoring-draft';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { api } from '../../lib/api-client';
import { usePageHelpDetails } from '../../lib/page-context';
import { emailApi, emailKey } from '../../lib/email-api';
import {
  resourceApi,
  emptyMessage,
  type Kind,
  type EmailResource,
  type TemplateDef,
  type AutomationDef,
  type MessageSpec,
  type Segment,
  type Recipient,
  type Condition,
  type Preview,
  type EnrollmentPreview,
  type Step,
} from '../../lib/email-resources-api';
import type { SchemaDescriptor } from '../../lib/api-types';
import { GmailSetupCard, GmailSetupResult, useGmailConnection } from './GmailConnection';
import { GmailAutomationLanding } from './GmailAutomationLanding';
import { GmailMark, MailSurface, MailMessage, MailSubject } from './MailSurface';
import { Icon } from '../ui/Icon';
import { isEmptySequenceStep, SequenceIntroduction, SequenceRow, SequenceStep, SequenceSettings, SequenceTemplateChoice } from './SequencePresentation';
import styles from './EmailAuthoring.module.css';
type ObjectDef = SchemaDescriptor['objects'][number];
type Field = ObjectDef['fields'][number];
const friendly = (s: string) => s.replaceAll('_', ' ');
const url = (kind: Kind, id?: string) =>
  `/app/email/${kind}${id ? `/${id}` : ''}`;

export function EmailWorkspace({
  kind,
  detail = false,
  run = false,
  create = false,
}: {
  kind: Kind;
  detail?: boolean;
  run?: boolean;
  create?: boolean;
}) {
  const params = useParams({ strict: false }) as {
    resourceId?: string;
    enrollmentId?: string;
  };
  const navigate = useNavigate();
  const [creatingTemplate, setCreatingTemplate] = useState(() =>
    new URLSearchParams(window.location.search).get('new') === '1',
  );
  // A saved working copy restores inside the creator, never in place of the library.
  const creating = kind === 'templates' ? creatingTemplate :
    !detail && !run && (create || new URLSearchParams(window.location.search).has('templateId'));
  const setCreating = (next: boolean) => {
    if (kind === 'templates') setCreatingTemplate(next);
    else if (next) void navigate({ to: '/app/email/automations/new', search: true });
    else void navigate({ to: '/app/email/automations', search: {} });
  };
  const editorOpen = kind === 'automations' && (creating || detail);
  const gmail = useGmailConnection();
  const checkingGmail = kind === 'automations' && !editorOpen && !run && gmail.isLoading;
  const showGmailLanding = kind === 'automations' && !editorOpen && !run && gmail.isSuccess &&
    !gmail.data.connections.some(connection => ['active', 'importing'].includes(connection.status));
  const options = useQuery({
    queryKey: [...emailKey(), 'options'],
    queryFn: resourceApi.options,
    retry: false,
  });
  return (
    <div className={styles.page}>
      {showGmailLanding && <div className={styles.gmailSetup}><GmailAutomationLanding /></div>}
      {checkingGmail && <p role="status">Checking Gmail…</p>}
      {!editorOpen && !showGmailLanding && !checkingGmail && <header className={`${styles.header} ${styles.pageHeader}`}>
        <h1>{kind === 'templates' ? 'Email Templates' : 'Email Automations'}</h1>
        {kind === 'automations' && <ConnectedGmailLink />}
        {kind === 'templates' && <a className={styles.libraryLink} href="/app/email/automations">← Email Automations</a>}
      </header>}
      {kind !== 'templates' && !editorOpen && !showGmailLanding && !checkingGmail && <SequenceGmailSetup />}
      {options.isError ? (
        <div role="alert">
          <p>{options.error.message}</p>
          <button type="button" disabled={options.isFetching} onClick={() => void options.refetch()}>
            Try again
          </button>
        </div>
      ) : options.isLoading ? (
        <p>Loading Email…</p>
      ) : options.data?.enabled === false ? (
        <section className={styles.introduction} aria-label={kind === 'templates' ? 'About templates' : 'About sequences'}>
          <span className={styles.introductionIcon}><Icon name={kind === 'templates' ? 'notes' : 'bolt'} size={24} /></span>
          <h2>{kind === 'templates' ? 'Your best messages, ready to use again' : 'Stay in touch at the right moment'}</h2>
          <p>{kind === 'templates'
            ? 'Save exact wording or give Echo writing guidance. Personalize each message with your contact’s details before preparing a draft.'
            : 'Prepare a message when a record changes, or plan a series of follow-ups. Each email waits for you to review and send.'}</p>
          <span className={styles.availability}>Not enabled yet</span>
          <p className={styles.availabilityHelp}>Your workspace administrator needs to enable templates and automations before you can create them.</p>
        </section>
      ) : run && params.enrollmentId ? (
        <RunView id={params.enrollmentId} />
      ) : detail && params.resourceId ? (
        <>
        {kind === 'templates' && <ResourceLibrary kind={kind} objects={options.data?.objects ?? []} creating={creating} setCreating={setCreating} />}
        <ResourceDetail
          key={params.resourceId}
          kind={kind}
          id={params.resourceId}
          objects={options.data?.objects ?? []}
        />
        </>
      ) : (
        <ResourceLibrary kind={kind} objects={options.data?.objects ?? []} creating={creating} setCreating={setCreating}
          showTemplates={gmail.isSuccess && !showGmailLanding}
          canCreate={kind === 'templates' || !!gmail.data?.connections.some(connection => ['active', 'importing'].includes(connection.status))} />
      )}
    </div>
  );
}
function ConnectedGmailLink() {
  const query = useGmailConnection();
  const connection = query.data?.connections.find(c => ['active', 'importing'].includes(c.status));
  if (query.isError || !connection) return null;
  return <a className={styles.connectedGmailLink} href="/app/settings/gmail"
    aria-label={`Gmail settings: connected as ${connection.accountEmail}`} title={`Connected as ${connection.accountEmail}`}>
    <GmailMark />
    <span className={styles.connectedGmailLabel}>Connected as</span>
    <span className={styles.connectedGmailAddress}>{connection.accountEmail}</span>
  </a>;
}
function SequenceGmailSetup() {
  const query = useGmailConnection();
  if (query.data?.connections.some(c => ['active', 'importing'].includes(c.status))) return <GmailSetupResult />;
  return <div className={styles.gmailSetup}><GmailSetupCard surface="sequences" /></div>;
}
function SequenceTemplates({ objects }: { objects: ObjectDef[] }) {
  const cards = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(3);
  useEffect(() => {
    const row = cards.current;
    if (!row) return;
    const measure = () => {
      // Reserve one card for New template, using the rendered card width.
      const card = row.firstElementChild as HTMLElement | null;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const available = row.clientWidth - 6;
      setVisibleCount(Math.max(0, Math.min(3,
        Math.floor((available + gap) / (card.getBoundingClientRect().width + gap)) - 1,
      )));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, []);
  const templates = useQuery({
    queryKey: [...emailKey(), 'templates', 'sequence-cards'],
    queryFn: () => resourceApi.list('templates'),
    retry: false,
  });
  const items = (templates.data?.items ?? []).slice(0, visibleCount);
  const hasMore = !!templates.data?.nextCursor || (templates.data?.items.length ?? 0) > visibleCount;
  const previewText = (segments: Segment[]) => segments.map(segment =>
    segment.kind === 'literal' ? segment.text : '[Contact detail]',
  ).join('');
  return <section className={styles.templateSection} aria-label="Email templates">
    <div className={styles.header}>
      <h2>Templates</h2>
      {hasMore && <a className={styles.libraryLink} href="/app/email/templates">More templates <span aria-hidden="true">→</span></a>}
    </div>
    {templates.isLoading && <p role="status" className={styles.hint}>Loading templates…</p>}
    {templates.isError && <p role="alert">Couldn’t load templates. <button type="button" disabled={templates.isFetching} onClick={() => void templates.refetch()}>Try again</button></p>}
    <div className={styles.templateCards} ref={cards}>
      {items.map(template => {
        const message = (template.definition as TemplateDef).message;
        const subject = message.mode === 'exact' ? previewText(message.subject) : message.purpose;
        const body = message.mode === 'exact' ? previewText(message.body) : message.instructions;
        return <TemplateEditButton key={template.id} objects={objects} template={template} className={styles.templateCard}>
          <span className={styles.templateCardTop}><Icon name="mail" size={18} /><span>{message.mode === 'exact' ? 'Your wording' : 'AI guidance'}</span></span>
          <h3>{template.name}</h3>
          <div className={styles.templateSnippet}>
            <strong>{subject || 'No subject yet'}</strong>
            <p>{body || 'Open this template to start writing.'}</p>
          </div>
          <span className={styles.templateCardFooter}>Edit template</span>
        </TemplateEditButton>;
      })}
      <TemplateCreateButton objects={objects} className={styles.templateCard}>
        <span className={styles.templateCardTop}><Icon name="mail" size={18} /><span>New</span></span>
        <h3>New template</h3>
        <div className={styles.templateSnippet}>
          <p>Create a new email template</p>
        </div>
        <span className={styles.templateCardFooter}>Create template</span>
      </TemplateCreateButton>
    </div>
  </section>;
}
function ResourceLibrary({
  kind,
  objects,
  creating,
  setCreating,
  showTemplates = true,
  canCreate = true,
}: {
  kind: Kind;
  objects: ObjectDef[];
  creating: boolean;
  setCreating: (creating: boolean) => void;
  showTemplates?: boolean;
  canCreate?: boolean;
}) {
  const [search, setSearch] = useState(''),
    [mode, setMode] = useState('all');
  const query = useInfiniteQuery({
    queryKey: [...emailKey(), kind, 'library', search, mode],
    enabled: !creating || kind === 'templates',
    queryFn: ({ pageParam }) =>
      resourceApi.list(kind, pageParam, { query: search, mode }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const browsing = !creating || kind === 'templates';
  return (
    <>
      {kind === 'automations' && browsing && showTemplates && <SequenceTemplates objects={objects} />}
      {browsing && <div className={styles.header}>
        <h2>{kind === 'templates' ? 'Templates' : 'Sequences'}</h2>
        {canCreate && !(kind === 'automations' && query.isSuccess && !items.length) && <button className={styles.primary} onClick={() => setCreating(!creating)}>
          New {kind === 'templates' ? 'template' : 'sequence'}
        </button>}
      </div>}
      {creating && kind === 'templates' && <TemplateCreateModal objects={objects} onClose={() => {
        setCreating(false);
        if (new URLSearchParams(window.location.search).has('new')) {
          const next = new URL(window.location.href);
          next.searchParams.delete('new');
          window.history.replaceState(null, '', next.pathname + next.search);
        }
      }} />}
      {creating && kind === 'automations' && (
        <ResourceEditor
          kind={kind}
          objects={objects}
          onSaved={(r) => window.location.assign(url(kind, r.id))}
        />
      )}
      {browsing && kind === 'templates' && <div className={styles.toolbar}>
        <label>
          Search
          <input value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        {kind === 'templates' && (
          <label>
            Mode
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="all">All modes</option>
              <option value="exact">Exact wording</option>
              <option value="guidance">AI guidance</option>
            </select>
          </label>
        )}
      </div>}
      {browsing && query.isError && <p role="alert">{query.error.message}</p>}
      {browsing && query.isSuccess && !items.length && (
        kind === 'templates'
          ? <div className={styles.empty}><Icon name="mail" size={30} /><h3>Your best messages, ready to reuse</h3><p>Save a message you can use again, or give Echo instructions to personalize it.</p></div>
          : <SequenceIntroduction onCreate={canCreate ? () => setCreating(true) : undefined} />
      )}
      {browsing && items
        .filter(
          (r) =>
            r.name.toLowerCase().includes(search.toLowerCase()) &&
            (mode === 'all' ||
              (r.definition as TemplateDef).message?.mode === mode),
        )
        .map((r) => kind === 'automations' ? (
          <SequenceRow key={r.id} resource={r}>{canCreate && <Duplicate resource={r} kind={kind} />}</SequenceRow>
        ) : (
          <article className={styles.row} key={r.id}>
            <div>
              <TemplateEditButton objects={objects} template={r} className={styles.templateTitle}>{r.name}</TemplateEditButton>
              <p className={styles.hint}>
                {(r.definition as TemplateDef).message.mode === 'exact' ? 'Exact wording' : 'AI guidance'}
                {' · '}Used by {r.usedBy ?? 0} sequences · Updated {new Date(r.updated_at).toLocaleDateString()}
              </p>
            </div>
            <Duplicate resource={r} kind={kind} />
          </article>
        ))}
      {browsing && query.hasNextPage && (
        <button onClick={() => void query.fetchNextPage()}>Load more</button>
      )}
    </>
  );
}
function Duplicate({
  resource: r,
  kind,
}: {
  resource: EmailResource;
  kind: Kind;
}) {
  const copy = useMutation({
    mutationFn: () =>
      resourceApi.save(kind, {
        name: `${r.name} copy`,
        definition: r.definition,
      }),
    onSuccess: (r) => window.location.assign(url(kind, r.id)),
  });
  return (
    <div>
      <button disabled={copy.isPending} onClick={() => copy.mutate()}>
        Duplicate
      </button>
      {copy.error && <p role="alert">{copy.error.message}</p>}
    </div>
  );
}
function ResourceDetail({
  kind,
  id,
  objects,
}: {
  kind: Kind;
  id: string;
  objects: ObjectDef[];
}) {
  const query = useQuery({
    queryKey: [...emailKey(), kind, id],
    queryFn: () => resourceApi.detail(kind, id),
  });
  if (query.isLoading) return <p>Loading…</p>;
  if (!query.data)
    return (
      <p role="alert">{query.error?.message ?? 'Resource unavailable.'}</p>
    );
  if (kind === 'templates') return <TemplateCreateModal objects={objects} resource={query.data}
    onClose={() => window.location.assign(url('templates'))} />;
  return (
    <ResourceEditor
      kind={kind}
      objects={objects}
      resource={query.data}
      onSaved={() => void query.refetch()}
    />
  );
}
function defaultDefinition(
  kind: Kind,
  objects: ObjectDef[],
  connectionId: string,
): TemplateDef | AutomationDef {
  const obj = objects.find((o) => o.name === 'person') ?? objects[0];
  const recipient = {
    emailFieldId: obj?.fields.find((f) => f.type === 'emails')?.id ?? '',
  };
  if (kind === 'templates')
    return {
      contextObjectId: obj?.id ?? '',
      recipient,
      message: emptyMessage(),
    };
  return {
    contextObjectId: obj?.id ?? '',
    recipient,
    connectionId,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    trigger: { kind: 'manual' },
    conditions: [],
    stopConditions: [],
    steps: [
      {
        id: crypto.randomUUID(),
        threadMode: 'new',
        message: { inline: emptyMessage() },
      },
    ],
    limits: { active: 100, daily: 20 },
  };
}
export function TemplateEditButton({ objects, template, className, children }: {
  objects: ObjectDef[];
  template: EmailResource;
  className?: string | undefined;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: [...emailKey(), 'templates', template.id],
    queryFn: () => resourceApi.detail('templates', template.id),
    enabled: open,
    refetchOnWindowFocus: false,
  });
  return <>
    <button type="button" className={className} onClick={() => setOpen(true)} aria-busy={open && query.isFetching}>{children}</button>
    {open && query.isFetching && <span role="status" className={styles.hint}>Loading template…</span>}
    {open && query.isError && <span role="alert">Couldn’t load template. <button type="button" onClick={() => void query.refetch()}>Try again</button></span>}
    {open && query.data && !query.isFetching && !query.isError && <TemplateCreateModal objects={objects} resource={query.data} onClose={() => setOpen(false)} />}
  </>;
}
function TemplateCreateButton({ objects, initialDefinition, onSaved, className, children }: {
  objects: ObjectDef[];
  initialDefinition?: TemplateDef | undefined;
  onSaved?: ((template: EmailResource) => void) | undefined;
  className?: string | undefined;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className={className} onClick={() => setOpen(true)}>{children}</button>
    {open && <TemplateCreateModal objects={objects} initialDefinition={initialDefinition} onSaved={onSaved} onClose={() => setOpen(false)} />}
  </>;
}
function TemplateCreateModal({ objects, initialDefinition, resource, onSaved, onClose }: {
  objects: ObjectDef[];
  initialDefinition?: TemplateDef | undefined;
  resource?: EmailResource | undefined;
  onSaved?: ((template: EmailResource) => void) | undefined;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  const description = useId();
  const client = useQueryClient();
  const [restored] = useState(() => initialDefinition ? undefined : readEmailWorkingCopy('templates', resource?.id));
  const [name, setName] = useState(restored?.name ?? resource?.name ?? '');
  const [definition, setDefinition] = useState<TemplateDef>(() => initialDefinition ??
    (restored?.definition as TemplateDef | undefined) ?? (resource?.definition as TemplateDef | undefined) ?? defaultDefinition('templates', objects, '') as TemplateDef);
  const [savedContent] = useState(() => JSON.stringify({
    name: resource?.name ?? '',
    definition: initialDefinition ?? resource?.definition ?? defaultDefinition('templates', objects, ''),
  }));
  const backdropPress = useRef(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => { if (element?.open) element.close(); };
  }, []);
  useEffect(() => {
    if (dirty && !initialDefinition) saveEmailWorkingCopy('templates', resource?.id, { name, definition });
  }, [dirty, initialDefinition, resource?.id, name, definition]);
  const save = useMutation({
    mutationFn: () => resourceApi.save('templates', { name, definition, ...(resource ? { expectedRevision: resource.revision } : {}) }, resource?.id),
    onSuccess: (template) => {
      if (!initialDefinition) saveEmailWorkingCopy('templates', resource?.id, null);
      void client.invalidateQueries({ queryKey: [...emailKey(), 'templates'] });
      onSaved?.(template);
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => resourceApi.remove('templates', resource!.id, resource!.revision),
    onSuccess: () => {
      saveEmailWorkingCopy('templates', resource?.id, null);
      void client.invalidateQueries({ queryKey: [...emailKey(), 'templates'] });
      onClose();
    },
  });
  const pending = save.isPending || remove.isPending;
  const hasEdits = JSON.stringify({ name, definition }) !== savedContent;
  const dismiss = () => {
    if (pending) return;
    if (hasEdits && !window.confirm('Discard unsaved changes? Your changes to this template will be lost.')) return;
    if (!initialDefinition) saveEmailWorkingCopy('templates', resource?.id, null);
    onClose();
  };
  const outsideDialog = (element: HTMLDialogElement, x: number, y: number) => {
    const bounds = element.getBoundingClientRect();
    return x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom;
  };
  const change = (next: TemplateDef) => { setDefinition(next); setDirty(true); };
  const context = objects.find(o => o.id === definition.contextObjectId);
  const recipient = definition.recipient.relationFieldId
    ? objects.find(o => o.id === context?.fields.find(f => f.id === definition.recipient.relationFieldId)?.relation?.targetObjectDefId)
    : context;
  return <dialog ref={dialog} className={styles.templateModal} aria-labelledby={title} aria-describedby={description}
    onCancel={e => { e.preventDefault(); dismiss(); }}
    onPointerDown={e => {
      backdropPress.current = e.target === e.currentTarget && outsideDialog(e.currentTarget, e.clientX, e.clientY);
    }}
    onClick={e => {
      const clickedBackdrop = backdropPress.current && e.target === e.currentTarget && outsideDialog(e.currentTarget, e.clientX, e.clientY);
      backdropPress.current = false;
      if (clickedBackdrop) dismiss();
    }}>
    <div className={styles.modalHeading}>
      <h2 id={title}>{resource ? 'Edit email template' : 'Create an email template'}</h2>
      <button type="button" aria-label="Close template editor" disabled={pending} onClick={dismiss}>×</button>
    </div>
    <p id={description} className={styles.hint}>Write an email or describe one for Echo. Save it with a name to reuse it in any sequence.</p>
    <fieldset className={styles.modalFields} disabled={pending}>
      <MessageEditor value={definition.message} onChange={message => change({ ...definition, message })} context={context} recipient={recipient} />
      <label className={styles.templateName}>Template name
        <input value={name} maxLength={200} placeholder="e.g. Proposal follow-up" onChange={e => { setName(e.target.value); setDirty(true); }} />
      </label>
      {!initialDefinition && <details className={styles.advanced}><summary>Personalization settings</summary>
        <label>Use details from<select value={definition.contextObjectId} onChange={e => {
          const object = objects.find(o => o.id === e.target.value);
          change({ ...definition, contextObjectId: e.target.value, recipient: { emailFieldId: object?.fields.find(f => f.type === 'emails')?.id ?? '' } });
        }}>{objects.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}</select></label>
        <RecipientEditor value={definition.recipient} onChange={value => change({ ...definition, recipient: value })} context={context} recipientObject={recipient} />
      </details>}
    </fieldset>
    {save.error && <p role="alert" className={styles.error}>{save.error.message}</p>}
    {remove.error && <p role="alert" className={styles.error}>{remove.error.message}</p>}
    {resource && <details className={styles.advanced}><summary>Delete template</summary>
      <p className={styles.hint}>Existing sequences keep the version they already use.</p>
      <button type="button" disabled={pending} onClick={() => remove.mutate()}>Delete this template</button>
    </details>}
    <div className={styles.modalActions}>
      <button type="button" disabled={pending} onClick={dismiss}>Cancel</button>
      <button type="button" className={styles.primary} disabled={pending || !name.trim()} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save template'}</button>
    </div>
  </dialog>;
}
export function SequenceChatEditor({ resource, onSaved, onClose }: {
  resource: EmailResource;
  onSaved: (resource: EmailResource) => void;
  onClose: () => void;
}) {
  const options = useQuery({ queryKey: [...emailKey(), 'options'], queryFn: resourceApi.options, retry: false });
  return <section className={`${styles.page} ${styles.chatEditor}`} aria-label="Edit sequence">
    {options.isError ? <><p role="alert">{options.error.message}</p><button onClick={() => void options.refetch()}>Try again</button></>
      : options.isLoading ? <p role="status">Loading sequence editor…</p>
      : options.data?.enabled ? <ResourceEditor key={resource.id} kind="automations" objects={options.data.objects}
        resource={resource} onSaved={onSaved} onClose={onClose} />
      : <p>Email automations are not enabled for this workspace.</p>}
    {!options.data?.enabled && <button onClick={onClose}>Back to sequence</button>}
  </section>;
}

function ResourceEditor({
  kind,
  objects,
  resource,
  onSaved,
  onClose,
}: {
  kind: Kind;
  objects: ObjectDef[];
  resource?: EmailResource;
  onSaved: (r: EmailResource) => void;
  onClose?: () => void;
}) {
  const client = useQueryClient();
  const [newStepId, setNewStepId] = useState<string>();
  const connections = useQuery({
    queryKey: [...emailKey(), 'connections'],
    queryFn: emailApi.connections,
  });
  const templates = useInfiniteQuery({
    queryKey: [...emailKey(), 'templates'],
    queryFn: ({ pageParam }) => resourceApi.list('templates', pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
    enabled: kind === 'automations',
  });
  const templateItems = templates.data?.pages.flatMap((p) => p.items) ?? [];
  const sourceTemplateId = new URLSearchParams(window.location.search).get(
    'templateId',
  );
  const sourceTemplate = useQuery({
    queryKey: [...emailKey(), 'templates', sourceTemplateId],
    queryFn: () => resourceApi.detail('templates', sourceTemplateId!),
    enabled: kind === 'automations' && !resource && !!sourceTemplateId,
  });
  const [restored] = useState(() => readEmailWorkingCopy(kind, resource?.id));
  const [name, setName] = useState(restored?.name ?? resource?.name ?? '');
  const [definition, setDefinition] = useState<TemplateDef | AutomationDef>(
    restored?.definition ?? resource?.definition ??
      defaultDefinition(
        kind,
        objects,
        connections.data?.connections[0]?.id ?? '',
      ),
  );
  const [dirty, setDirty] = useState(!!restored),
    [preview, setPreview] = useState<Preview>(),
    [tab, setTab] = useState('edit');
  const [baseRevision, setBaseRevision] = useState(restored?.baseRevision ?? resource?.revision);
  const [recordId, setRecordId] = useState(
    new URLSearchParams(window.location.search).get('recordId') ?? '',
  );
  const [connectionId, setConnectionId] = useState(
    connections.data?.connections[0]?.id ?? '',
  );
  const [publishedPreview, setPublishedPreview] = useState(false);
  const [generationRequested, setGenerationRequested] = useState(false);
  const [notice, setNotice] = useState(restored ? 'Your unsaved email has been restored.' : '');
  usePageHelpDetails({
    view: 'editor',
    kind,
    resourceId: resource?.id,
    name,
    definition,
    hasUnsavedChanges: dirty,
    state: resource?.state,
    publishedVersionId: resource?.published_version_id,
    activeTab: tab,
    contextObject: objects.find((object) => object.id === definition.contextObjectId),
  });
  useEffect(() => { if (dirty) saveEmailWorkingCopy(kind, resource?.id, { name, definition, ...(baseRevision !== undefined ? { baseRevision } : {}) }); }, [dirty, kind, resource?.id, name, definition, baseRevision]);
  useEffect(() => {
    if (resource && !dirty) {
      setName(resource.name);
      setDefinition(resource.definition);
      setBaseRevision(resource.revision);
    }
  }, [resource?.revision]);
  useEffect(() => {
    const first = connections.data?.connections[0]?.id;
    if (first) {
      if (!connectionId) setConnectionId(first);
      if ('connectionId' in definition && !definition.connectionId)
        setDefinition({ ...definition, connectionId: first });
    }
  }, [connections.data]);
  useEffect(() => {
    const t = sourceTemplate.data;
    if (!resource && t?.published_version_id && !dirty) {
      const v = t.versions?.find((v) => v.id === t.published_version_id);
      if (v) {
        const d = v.definition as TemplateDef;
        setDefinition({
          ...defaultDefinition('automations', objects, connectionId),
          contextObjectId: d.contextObjectId,
          recipient: d.recipient,
          steps: [
            {
              id: crypto.randomUUID(),
              threadMode: 'new',
              message: { templateVersionId: t.published_version_id },
            },
          ],
        } as AutomationDef);
        setName(`${t.name} sequence`);
      }
    }
  }, [sourceTemplate.data]);
  const change = (d: TemplateDef | AutomationDef) => {
    setDefinition(d);
    setDirty(true);
    setPreview(undefined);
    setGenerationRequested(false);
  };
  const context = objects.find((o) => o.id === definition.contextObjectId);
  const recipientObject = definition.recipient.relationFieldId
    ? objects.find(
        (o) =>
          o.id ===
          context?.fields.find(
            (f) => f.id === definition.recipient.relationFieldId,
          )?.relation?.targetObjectDefId,
      )
    : context;
  const save = useMutation({
    mutationFn: () =>
      resourceApi.save(
        kind,
        {
          name,
          definition,
          ...(resource ? { expectedRevision: baseRevision ?? resource.revision } : {}),
        },
        resource?.id,
      ),
    onSuccess: (r) => {
      saveEmailWorkingCopy(kind, resource?.id, null);
      setDirty(false);
      setBaseRevision(r.revision);
      setNotice(
        kind === 'templates'
          ? 'Template saved and ready to use.'
          : 'Working copy saved. Published versions and existing drafts are unchanged.',
      );
      onSaved(r);
      void client.invalidateQueries({ queryKey: [...emailKey(), kind] });
    },
  });
  const action = useMutation({
    mutationFn: async (command: string) => {
      if (!resource) throw new Error('Save the working copy first.');
      if (dirty)
        throw new Error(
          'Save this working copy before publishing or activating.',
        );
      if (command === 'publish' || command === 'activate')
        return resourceApi.publish(
          kind,
          resource.id,
          resource.revision,
          command === 'activate',
        );
      if (command === 'delete')
        return resourceApi.remove(kind, resource.id, resource.revision);
      return resourceApi.automationCommand(
        resource.id,
        command as 'pause' | 'resume',
        resource.revision,
      );
    },
    onSuccess: (_r, command) => {
      setPreview(undefined);
      if (command === 'delete') {
        saveEmailWorkingCopy(kind, resource?.id, null);
        if (onClose) { onClose(); void client.invalidateQueries({ queryKey: emailKey() }); }
        else window.location.assign(url(kind));
        return;
      }
      setNotice(
        command === 'activate'
          ? 'Active. Applies to future events only. Each prepared email still requires approval.'
          : 'Saved. Existing runs keep their pinned versions.',
      );
      onSaved(resource!);
      void client.invalidateQueries({ queryKey: emailKey() });
    },
  });
  const previewAction = useMutation({
    mutationFn: (generate: boolean) => {
      if (!resource || dirty)
        throw new Error(kind === 'templates' ? 'Save the template before previewing.' : 'Save the working copy before previewing.');
      if (!recordId) throw new Error('Choose a context record.');
      const regenerate = generate && generationRequested;
      if (generate) setGenerationRequested(true);
      return resourceApi.preview(kind, resource.id, {
        recordId,
        connectionId,
        expectedRevision: resource.revision,
        generate,
        published: kind === 'templates' || publishedPreview,
        regenerate,
      });
    },
    onSuccess: (p) => {
      setPreview(p);
      setTab('preview');
    },
  });
  const isTemplate = 'message' in definition;
  const newSequence = !isTemplate && !resource;
  const emptySequence = newSequence && definition.steps.every(isEmptySequenceStep);
  return (
    <>
      <div className={styles.header}>
        {onClose ? <button type="button" disabled={save.isPending || action.isPending} onClick={onClose}>Back to sequence</button> : <a href={url('automations')}>
          ← Back to Email Automations
        </a>}
        {!isTemplate && resource && <span>
          {resource?.state ?? 'Draft'} · Revision {resource?.revision ?? 'new'}
          {resource?.versions?.[0]
            ? ` · Published v${resource.versions[0].version}`
            : ''}
        </span>}
      </div>
      {dirty && resource && baseRevision !== resource.revision && <div role="status">
        <p>This sequence changed while you were editing. Your edits are preserved; saving will not overwrite the newer version.</p>
        <button type="button" disabled={save.isPending || action.isPending} onClick={() => {
          setName(resource.name); setDefinition(resource.definition); setBaseRevision(resource.revision);
          setDirty(false); setPreview(undefined); save.reset();
          saveEmailWorkingCopy(kind, resource.id, null);
        }}>Discard my edits and load saved version</button>
      </div>}
      {isTemplate && <div className={styles.templateIntro}>
        <h1>{resource ? 'Edit email template' : 'Create an email template'}</h1>
        <p>Write an email or describe one for Echo to write. Save it with a name so you can use it again.</p>
      </div>}
      {!isTemplate && <div className={styles.builderHeading}>
        <div><h1>{resource ? 'Your sequence' : 'Create a sequence'}</h1>
        <p>Build your email flow. Choose a template or write with Echo, then add your next step.</p></div>
        {resource && <button type="button" onClick={() => setTab(tab === 'preview' ? 'edit' : 'preview')}>{tab === 'preview' ? 'Hide preview' : 'Preview with a contact'}</button>}
      </div>}
      {!isTemplate && !onClose && <div className={styles.sequenceHelpTip}>
        <Icon name="sparkles" size={18} />
        <p>Or just click <strong>Help</strong> and tell Echo what kind of sequence you want.</p>
      </div>}
      <div className={`${styles.layout} ${isTemplate ? styles.templateEditor : styles.sequenceEditor}`} data-tab="edit">
        <section>
          {!isTemplate && <label className={styles.sequenceName}>
            Sequence name
            <input
              value={name}
              placeholder="e.g. Proposal follow-up"
              maxLength={200}
              onChange={(e) => {
                setName(e.target.value);
                setDirty(true);
              }}
            />
          </label>}
          {isTemplate && <>
            <MessageEditor
              value={definition.message}
              onChange={(message) => change({ ...definition, message })}
              context={context}
              recipient={recipientObject}
            />
            <label className={styles.templateName}>
              Template name
              <input value={name} maxLength={200} placeholder="e.g. Proposal follow-up"
                onChange={(e) => { setName(e.target.value); setDirty(true); }} />
            </label>
          </>}
          {isTemplate && <div>
          <TemplateSettings collapsed={isTemplate}>
          <label>
            {isTemplate ? 'Use details from' : 'Who is this sequence for?'}
            <select
              value={definition.contextObjectId}
              onChange={(e) => {
                const o = objects.find((o) => o.id === e.target.value);
                change({
                  ...definition,
                  contextObjectId: e.target.value,
                  recipient: {
                    emailFieldId:
                      o?.fields.find((f) => f.type === 'emails')?.id ?? '',
                  },
                });
                setRecordId('');
              }}
            >
              {objects.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <details className={styles.advanced}><summary>Choose a different recipient or email field</summary><RecipientEditor
            value={definition.recipient}
            onChange={(r) => change({ ...definition, recipient: r })}
            context={context}
            recipientObject={recipientObject}
          /></details>
          </TemplateSettings>
          </div>}
          {!isTemplate && (
            <>
              <div>
              <div className={styles.builderCanvas} data-empty={emptySequence} role="region" aria-label="Sequence flow canvas" tabIndex={0}>
              <div className={styles.builderTrack}>
              <SequenceSettings label="Choose who & when" summary={`${context?.labelPlural ?? context?.label ?? 'Contacts'} · ${definition.trigger.kind === 'manual' ? 'You choose who to enroll' : definition.trigger.kind === 'created' ? 'When a record is created' : 'When a field changes'}`}>
          <div>
          <TemplateSettings collapsed={isTemplate}>
          <label>
            {isTemplate ? 'Use details from' : 'Who is this sequence for?'}
            <select
              value={definition.contextObjectId}
              onChange={(e) => {
                const o = objects.find((o) => o.id === e.target.value);
                change({
                  ...definition,
                  contextObjectId: e.target.value,
                  recipient: {
                    emailFieldId:
                      o?.fields.find((f) => f.type === 'emails')?.id ?? '',
                  },
                });
                setRecordId('');
              }}
            >
              {objects.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <details className={styles.advanced}><summary>Choose a different recipient or email field</summary><RecipientEditor
            value={definition.recipient}
            onChange={(r) => change({ ...definition, recipient: r })}
            context={context}
            recipientObject={recipientObject}
          /></details>
          </TemplateSettings>
          </div>
              <div>
              {(connections.data?.connections.length ?? 0) !== 1 && <label>
                Send from
                <select
                  value={definition.connectionId}
                  onChange={(e) =>
                    change({ ...definition, connectionId: e.target.value })
                  }
                >
                  <option value="">Choose account</option>
                  {connections.data?.connections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.accountEmail} · {friendly(c.status)}
                    </option>
                  ))}
                </select>
              </label>}
              {connections.isError && <p role="alert">Couldn’t load your email accounts. <button type="button" onClick={() => void connections.refetch()}>Try again</button></p>}
              <TriggerEditor
                definition={definition}
                fields={context?.fields ?? []}
                onChange={change}
              />
              <details className={styles.advanced}><summary>Only include matching records</summary><ConditionEditor
                title="Only when all conditions match"
                value={definition.conditions}
                fields={context?.fields ?? []}
                onChange={(conditions) => change({ ...definition, conditions })}
              /></details>
              </div>
              </SequenceSettings>
              <ol className={styles.builderSteps} aria-label="Sequence email flow">
              {(emptySequence ? definition.steps.slice(0, 1) : definition.steps).map((step, index) => (
                <SequenceStep key={step.id} empty={emptySequence} step={step} index={index} templates={templateItems} autoOpen={newStepId === step.id}
                  onChange={next => change({ ...definition, steps: (emptySequence ? [step] : definition.steps).map(s => s.id === step.id ? next : s) })}
                  onRemove={index > 0 ? () => change({ ...definition, steps: definition.steps.filter(s => s.id !== step.id) }) : undefined}>
                  {(step, updateStep) => <section className={styles.step}>
                  {index > 0 && (
                    <>
                      <DelayEditor
                        value={
                          step.afterPreviousSend ?? {
                            amount: 3,
                            unit: 'business_days',
                          }
                        }
                        onChange={(afterPreviousSend) =>
                          updateStep({ ...step, afterPreviousSend })
                        }
                      />
                      <p className={styles.hint}>
                        Echo waits this long after you send the previous email. If they reply, this follow-up is skipped.
                      </p>
                    </>
                  )}
                  {index > 0 && <details className={styles.advanced}><summary>Conversation settings</summary><label>
                    Where should this follow-up appear?
                    <select
                      value={step.threadMode}
                      disabled={index === 0}
                      onChange={(e) =>
                        updateStep({ ...step, threadMode: e.target.value as Step['threadMode'] })
                      }
                    >
                      <option value="new">Start a new thread</option>
                      {index > 0 && (
                        <option value="reply_previous">
                          Reply in the previous sequence thread
                        </option>
                      )}
                    </select>
                  </label></details>}
                  <div className={styles.stepTemplatePicker}><h3>Start with a saved template</h3>
                  <div className={styles.stepTemplateChoices}>
                    {templateItems.filter(t => t.published_version_id && t.definition.contextObjectId === definition.contextObjectId).map(t => {
                      const message = (t.definition as TemplateDef).message;
                      return <button type="button" key={t.id} className={styles.templateChoice}
                        aria-pressed={'templateVersionId' in step.message && step.message.templateVersionId === t.published_version_id}
                        onClick={() => updateStep({ ...step, message: { templateVersionId: t.published_version_id! } })}>
                        <span className={styles.templateCardTop}><Icon name={message.mode === 'guidance' ? 'sparkles' : 'mail'} size={16} /><span>{message.mode === 'guidance' ? 'AI guidance' : 'Your wording'}</span></span>
                        <strong>{t.name}</strong>
                      </button>;
                    })}
                  </div>
                  {'templateVersionId' in step.message && <button type="button" onClick={() => updateStep({ ...step, message: { inline: emptyMessage() } })}>Write a new email instead</button>}
                  {templates.isLoading && <p role="status">Loading templates…</p>}
                  {templates.isSuccess && !templateItems.some(t => t.published_version_id && t.definition.contextObjectId === definition.contextObjectId) && <p className={styles.hint}>No templates for this audience yet. Write below or describe an email for Echo.</p>}
                  <TemplateCreateButton objects={objects} initialDefinition={{
                    contextObjectId: definition.contextObjectId,
                    recipient: definition.recipient,
                    message: emptyMessage(),
                  }} onSaved={template => {
                    if (template.published_version_id) updateStep({ ...step, message: { templateVersionId: template.published_version_id } });
                  }}>Create a new template</TemplateCreateButton>
                  {templates.isError && <p role="alert">Couldn’t load templates. <button type="button" onClick={() => void templates.refetch()}>Try again</button></p>}
                  {templates.hasNextPage && <button type="button" onClick={() => void templates.fetchNextPage()}>Load more templates</button>}
                  </div>
                  {'inline' in step.message ? (
                    <>
                      <MessageEditor
                        value={step.message.inline}
                        onChange={(inline) =>
                          updateStep({ ...step, message: { inline } })
                        }
                        context={context}
                        recipient={recipientObject}
                        hideSubject={step.threadMode === 'reply_previous'}
                      />
                      <TemplateCreateButton objects={objects} initialDefinition={{
                        contextObjectId: definition.contextObjectId,
                        recipient: definition.recipient,
                        message: step.message.inline,
                      }} onSaved={template => {
                        if (template.published_version_id) updateStep({ ...step, message: { templateVersionId: template.published_version_id } });
                      }}>Save this email as a template</TemplateCreateButton>
                    </>
                  ) : (
                    <SequenceTemplateChoice step={step} templates={templateItems} onCustomize={message => updateStep({ ...step, message: { inline: message } })} />
                  )}
                  <p className={styles.hint}>
                    You review and send this email from Home.
                  </p>
                </section>}
                </SequenceStep>
              ))}
              <li className={styles.builderAddStep}>
              <button type="button" className={styles.addEmailCard}
                disabled={definition.steps.length >= 10}
                onClick={() => {
                  const id = crypto.randomUUID();
                  setNewStepId(id);
                  change({ ...definition, steps: [...definition.steps, {
                    id, threadMode: 'reply_previous',
                    afterPreviousSend: { amount: 3, unit: 'business_days' },
                    message: { inline: emptyMessage() },
                  }] });
                }}
              >
                <span aria-hidden="true">＋</span><strong>Add follow-up</strong><small>Choose a template or write an email</small>
              </button>
              </li>
              </ol>
              </div>
              </div>
              <p className={styles.hint}>Each email arrives in Home for review. A reply stops the remaining follow-ups.</p>
              </div>
              <div>
              <details className={styles.sequenceSettings}>
                <summary>
                  <span><strong>Sequence settings</strong><small>Stop rules, timing, and limits</small></span>
                  <Icon name="chevron-down" size={18} />
                </summary>
                <div className={styles.sequenceSettingsGrid}>
                  <section className={styles.sequenceSettingCard}>
                    <h3>When to stop</h3>
                    <p>Follow-ups stop when someone replies in this sequence.</p>
                    <ul>
                      <li>Other emails with this person pause the sequence for review.</li>
                      <li>Choosing Done on a draft ends their sequence.</li>
                    </ul>
                    <details className={styles.settingExtras} open={definition.stopConditions.length > 0 || undefined}>
                      <summary>Custom stop rules{definition.stopConditions.length ? ` (${definition.stopConditions.length})` : ''}</summary>
                      <ConditionEditor title="Stop if any rule matches" value={definition.stopConditions} fields={context?.fields ?? []}
                        onChange={stopConditions => change({ ...definition, stopConditions })} />
                    </details>
                  </section>
                  <section className={styles.sequenceSettingCard}>
                    <h3>Timing</h3>
                    <p>Choose the time zone used for the waits between emails.</p>
                    <label>Time zone
                      <input value={definition.timezone} spellCheck={false} placeholder="America/New_York"
                        onChange={e => change({ ...definition, timezone: e.target.value })} />
                    </label>
                    <p className={styles.settingNote}>Day-based waits keep the same local time. Business days count Monday–Friday, including holidays.</p>
                    {resource && <p className={styles.settingNote}>People already enrolled keep their original time zone.</p>}
                  </section>
                  <section className={styles.sequenceSettingCard}>
                    <h3>Limits</h3>
                    <p>Control how much this sequence can have in progress.</p>
                    <div className={styles.sequenceLimitFields}>
                      <label>Active enrollments
                        <input type="number" min={1} max={100} value={definition.limits.active}
                          onChange={e => change({ ...definition, limits: { ...definition.limits, active: Number(e.target.value) } })} />
                        <span>Up to 100 at a time</span>
                      </label>
                      <label>New drafts per day
                        <input type="number" min={1} max={20} value={definition.limits.daily}
                          onChange={e => change({ ...definition, limits: { ...definition.limits, daily: Number(e.target.value) } })} />
                        <span>Up to 20 · resets at midnight UTC</span>
                      </label>
                    </div>
                    <details className={styles.settingExtras}><summary>Which records can enter?</summary>
                      <p>Automatic enrollment uses new, authorized CRM changes. Imports and changes made while paused won’t add people.</p>
                      <p>A record enters only once, even if you update the sequence. Restart it explicitly to run it again.</p>
                    </details>
                  </section>
                </div>
              </details>
              </div>
            </>
          )}
          <div className={isTemplate ? styles.toolbar : styles.sequenceSaveBar}>
            {newSequence && name.trim() && <div className={styles.sequenceSaveCopy}>
              <strong>Ready to save your plan?</strong>
              <span>Save now. Choose who to enroll when you activate it.</span>
            </div>}
            <button
              className={styles.primary}
              disabled={save.isPending || !name.trim()}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : isTemplate ? 'Save template' : newSequence ? 'Save sequence' : 'Save draft'}
            </button>
            {resource && (
              <>
                {!isTemplate && <button
                  disabled={dirty || action.isPending}
                  onClick={() => action.mutate('publish')}
                >
                  Publish changes
                </button>}
                {!isTemplate && (
                  <>
                    <button
                      disabled={dirty || action.isPending || !connections.data?.connections.some(c => c.id === definition.connectionId && c.status === 'active')}
                      onClick={() =>
                        action.mutate(
                          resource.state === 'active'
                            ? 'pause'
                            : resource.state === 'paused'
                              ? 'resume'
                              : 'activate',
                        )
                      }
                    >
                      {resource.state === 'active'
                        ? 'Pause'
                        : resource.state === 'paused'
                          ? 'Resume'
                          : 'Activate'}
                    </button>
                    <p className={styles.hint}>
                      Applies to future events only.{' '}
                      {resource.existingRuns ?? 0} existing runs keep their
                      published version.
                    </p>
                  </>
                )}
                <details>
                  <summary>
                    Delete {isTemplate ? 'template' : 'sequence'}
                  </summary>
                  <p>
                    {isTemplate
                      ? 'Pinned versions remain available to existing runs.'
                      : 'Stops all runs and retains send history.'}
                  </p>
                  <button
                    disabled={action.isPending}
                    onClick={() => action.mutate('delete')}
                  >
                    Delete this {isTemplate ? 'template' : 'sequence'}
                  </button>
                </details>
              </>
            )}
          </div>
          {isTemplate && resource && !resource.published_version_id && <p className={styles.hint}>Save this template to make it ready to use.</p>}
          {resource?.dependencies?.length ? (
            <section>
              <h2>Used by</h2>
              {resource.dependencies.map((a) => (
                <p key={a.id}>
                  <a href={url('automations', a.id)}>{a.name}</a> ·{' '}
                  {(a.published_definition ?? a.definition).steps.some(
                    (s) =>
                      'templateVersionId' in s.message &&
                      s.message.templateVersionId !==
                        resource.published_version_id,
                  )
                    ? 'Newer template available — update this sequence to use it'
                    : 'Using the latest saved template'}
                </p>
              ))}
            </section>
          ) : null}
          {isTemplate && resource?.published_version_id && (
            <a href={`${url('automations')}?templateId=${resource.id}`}>
              Create sequence using this template
            </a>
          )}
        </section>
        {!isTemplate && resource && tab === 'preview' && <aside className={styles.panel} aria-label="Message preview">
          <h2>Preview with…</h2>
          <RecordPicker
            object={context}
            value={recordId}
            onChange={(id) => {
              setRecordId(id);
              setPreview(undefined);
            }}
          />
          {!isTemplate && resource?.published_version_id && (
            <label>
              Preview published version
              <input
                type="checkbox"
                checked={publishedPreview}
                onChange={(e) => {
                  setPublishedPreview(e.target.checked);
                  setPreview(undefined);
                }}
              />
            </label>
          )}
          <p className={styles.hint}>
            Preview never sends or enrolls anyone. Exact wording uses no AI.
            AI previews use credits within your email writing limit.
          </p>
          <button
            disabled={
              dirty || !resource || previewAction.isPending || !recordId
            }
            onClick={() => previewAction.mutate(false)}
          >
            Preview fields and eligibility
          </button>
          {('inline' in definition.steps[0]!.message
              ? definition.steps[0]!.message.inline.mode === 'guidance'
              : true) && (
            <button
              disabled={
                dirty || !resource || previewAction.isPending || !recordId
              }
              onClick={() => previewAction.mutate(true)}
            >
              {preview?.result.inference
                ? 'Regenerate preview · AI usage applies'
                : 'Generate preview · AI usage applies'}
            </button>
          )}
          {preview && (
            <>
              <p>
                To: {preview.snapshot.resolved.recipient.name} ·{' '}
                {preview.snapshot.resolved.address}
              </p>
              <p className={styles.hint}>
                Context: {preview.snapshot.resolved.context.name}
              </p>
              {preview.result.trigger && (
                <p>{preview.result.trigger.explanation}</p>
              )}
              {preview.result.missing?.map((m, i) => (
                <p role="alert" key={i}>
                  {m.message}
                </p>
              ))}
              {preview.result.needsGeneration ? (
                <p>Choose Generate preview to create a sample.</p>
              ) : (
                <MailSurface label="Preview · not sent">
                  <MailSubject subject={preview.result.subject || '(No subject)'} />
                  <MailMessage from="Draft preview" to={preview.snapshot.resolved.address} subject={preview.result.subject} body={preview.result.bodyText} />
                  {preview.result.reason && <p>{preview.result.reason}</p>}
                </MailSurface>
              )}
              {!isTemplate && (
                <p>
                  Follow-ups wait for sending, then use the delays and stop
                  conditions shown in the editor.
                </p>
              )}
            </>
          )}
        </aside>}
      </div>
      {[save.error, action.error, previewAction.error]
        .filter(Boolean)
        .map((e, i) => (
          <p className={styles.error} role="alert" key={i}>
            {e!.message}
          </p>
        ))}
      {notice && <p role="status">{notice}</p>}
      {!isTemplate && resource?.published_version_id && (
        <>
          <ManualEnrollment
            automation={resource as EmailResource<AutomationDef>}
            object={context}
          />
          <RunList id={resource.id} />
        </>
      )}
    </>
  );
}
function TemplateSettings({ collapsed, children }: { collapsed: boolean; children: ReactNode }) {
  return collapsed
    ? <details className={styles.advanced}><summary>Personalization settings</summary><p className={styles.hint}>Choose which records and email fields this template uses. These default to People when available.</p>{children}</details>
    : <>{children}</>;
}
function RecipientEditor({
  value,
  onChange,
  context,
  recipientObject,
}: {
  value: Recipient;
  onChange: (r: Recipient) => void;
  context?: ObjectDef | undefined;
  recipientObject?: ObjectDef | undefined;
}) {
  return (
    <fieldset className={styles.panel}>
      <legend>Recipient</legend>
      <label>
        Who
        <select
          value={value.relationFieldId ?? 'self'}
          onChange={(e) =>
            onChange(
              e.target.value === 'self'
                ? {
                    emailFieldId:
                      context?.fields.find((f) => f.type === 'emails')?.id ??
                      '',
                  }
                : { relationFieldId: e.target.value, emailFieldId: '' },
            )
          }
        >
          <option value="self">{context?.label ?? 'Selected record'} directly</option>
          {context?.fields
            .filter((f) => f.type === 'relation')
            .map((f) => (
              <option key={f.id} value={f.id}>
                {context.label} → {f.label}
              </option>
            ))}
        </select>
      </label>
      <label>
        Email field
        <select
          value={value.emailFieldId}
          onChange={(e) => onChange({ ...value, emailFieldId: e.target.value })}
        >
          <option value="">Choose email field</option>
          {recipientObject?.fields
            .filter((f) => f.type === 'emails')
            .map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
        </select>
      </label>
      <label>
        Address choice
        <select
          value={value.address ? 'explicit' : 'primary'}
          onChange={(e) => {
            const next = { ...value };
            if (e.target.value === 'primary') delete next.address;
            else next.address = '';
            onChange(next);
          }}
        >
          <option value="primary">Declared primary or sole address</option>
          <option value="explicit">Specific existing structured address</option>
        </select>
      </label>
      {value.address !== undefined && (
        <label>
          Existing address
          <input
            value={value.address}
            onChange={(e) => onChange({ ...value, address: e.target.value })}
          />
        </label>
      )}
      <p className={styles.hint}>
        Multiple related people or ambiguous addresses need input. No address is
        inferred.
      </p>
    </fieldset>
  );
}
export function MessageEditor({
  value,
  onChange,
  context,
  recipient,
  hideSubject = false,
}: {
  value: MessageSpec;
  onChange: (m: MessageSpec) => void;
  context?: ObjectDef | undefined;
  recipient?: ObjectDef | undefined;
  hideSubject?: boolean;
}) {
  const [savedModes, setSavedModes] = useState<
    Partial<Record<MessageSpec['mode'], MessageSpec>>
  >({});
  const fields = [
    ...(context?.fields ?? []).map((f) => ({
      ...f,
      source: 'context' as const,
    })),
    ...(recipient?.fields ?? []).map((f) => ({
      ...f,
      source: 'recipient' as const,
    })),
  ].filter((f) =>
    [
      'text',
      'markdown',
      'number',
      'boolean',
      'select',
      'date',
      'datetime',
      'currency',
      'rating',
      'url',
    ].includes(f.type),
  );
  const chooseMode = (mode: MessageSpec['mode']) => {
    if (mode === value.mode) return;
    setSavedModes({ ...savedModes, [value.mode]: value });
    onChange(savedModes[mode] ?? (mode === 'exact' ? emptyMessage() : {
      mode: 'guidance', purpose: '', instructions: '',
      requiredContext: [{ kind: 'context' }, { kind: 'recipient' }],
      optionalContext: [],
    }));
  };
  return (
    <div>
      <div className={styles.writingChoices} role="group" aria-label="How would you like to create this email?">
        <button type="button" aria-pressed={value.mode === 'exact'} onClick={() => chooseMode('exact')}>
          <strong>Write an email</strong><span>Save your wording to use again.</span>
        </button>
        <button type="button" aria-pressed={value.mode === 'guidance'} onClick={() => chooseMode('guidance')}>
          <strong>Describe an email</strong><span>Echo writes a personalized draft each time.</span>
        </button>
      </div>
    <MailSurface label={value.mode === 'exact' ? 'Compose email' : 'AI writing instructions'}>
      {value.mode === 'exact' ? (
        <>
          <p className={styles.hint}>
            Your words, with contact details filled in where you insert a field. No AI credits used.
          </p>
          {!hideSubject && (
            <SegmentEditor
              label="Subject"
              segments={value.subject}
              fields={fields}
              onChange={(subject) => onChange({ ...value, subject })}
            />
          )}
          {hideSubject && <p>Subject follows the existing thread.</p>}
          <SegmentEditor
            label="Body"
            segments={value.body}
            fields={fields}
            onChange={(body) => onChange({ ...value, body })}
          />
        </>
      ) : (
        <>
          <label>
            What is this email for?
            <input
              value={value.purpose}
              placeholder="e.g. Follow up after sending a proposal"
              maxLength={1000}
              onChange={(e) => onChange({ ...value, purpose: e.target.value })}
            />
          </label>
          <label>
            What should Echo say?
            <textarea
              value={value.instructions}
              rows={7}
              placeholder="Ask whether they’ve had a chance to review the proposal. Offer to answer questions. Keep it warm, brief, and low pressure."
              maxLength={8000}
              onChange={(e) =>
                onChange({ ...value, instructions: e.target.value })
              }
            />
          </label>
          <details className={styles.advanced}><summary>Add an example of your writing (optional)</summary><label>
            Example email · Echo will use its style, not its facts
            <textarea
              value={value.example ?? ''}
              maxLength={8000}
              onChange={(e) => onChange({ ...value, example: e.target.value })}
            />
          </label></details>
          <details><summary>Context and notes for Echo</summary>
          {['context', 'recipient', 'correspondence'].map((kind) => {
            const state = value.requiredContext.some((s) => s.kind === kind)
              ? 'required'
              : value.optionalContext.some((s) => s.kind === kind)
                ? 'optional'
                : 'none';
            return (
              <label key={kind}>
                {friendly(kind)} context
                <select
                  value={state}
                  onChange={(e) => {
                    const requiredContext = value.requiredContext.filter(
                        (s) => s.kind !== kind,
                      ),
                      optionalContext = value.optionalContext.filter(
                        (s) => s.kind !== kind,
                      );
                    if (e.target.value === 'required')
                      requiredContext.push({
                        kind: kind as
                          | 'context'
                          | 'recipient'
                          | 'correspondence',
                      });
                    if (e.target.value === 'optional')
                      optionalContext.push({
                        kind: kind as
                          | 'context'
                          | 'recipient'
                          | 'correspondence',
                      });
                    onChange({ ...value, requiredContext, optionalContext });
                  }}
                >
                  <option value="none">Do not include</option>
                  <option value="required">Required</option>
                  <option value="optional">Optional</option>
                </select>
              </label>
            );
          })}
          <NoteSelectors message={value} onChange={onChange} />
          </details>
          <p className={styles.hint}>
            You’re saving instructions. Echo uses them to write a fresh draft when you use this template. You review every email before sending.
          </p>
        </>
      )}
    </MailSurface>
    </div>
  );
}
function SegmentEditor({
  label,
  segments,
  fields,
  onChange,
}: {
  label: string;
  segments: Segment[];
  fields: Array<Field & { source: 'context' | 'recipient' }>;
  onChange: (s: Segment[]) => void;
}) {
  const [field, setField] = useState('');
  return (
    <section aria-label={label}>
      {segments.map((s, i) =>
        s.kind === 'literal' ? (
          <label key={i}>
            {segments.length === 1 ? label : `${label} · text ${i + 1}`}

            <textarea
              value={s.text}
              rows={label === 'Subject' ? 1 : 7}
              placeholder={label === 'Subject' ? 'Subject' : 'Write your email…'}
              onChange={(e) =>
                onChange(
                  segments.map((v, j) =>
                    i === j ? { kind: 'literal', text: e.target.value } : v,
                  ),
                )
              }
            />
          </label>
        ) : (
          <div className={styles.token} key={i}>
            <strong>
              {s.source === 'context' ? 'Context' : 'Recipient'} →{' '}
              {fields.find((f) => f.id === s.fieldId)?.label ?? 'Missing field'}
            </strong>
            <label>
              Format
              <select
                value={s.format}
                onChange={(e) =>
                  onChange(
                    segments.map((v, j) =>
                      i === j
                        ? {
                            ...s,
                            format: e.target.value as
                              | 'text'
                              | 'date'
                              | 'currency',
                          }
                        : v,
                    ),
                  )
                }
              >
                <option value="text">Text</option>
                <option value="date">Date</option>
                <option value="currency">Currency</option>
              </select>
            </label>
            {s.format === 'currency' && (
              <label>
                Currency code
                <input
                  value={s.currency ?? ''}
                  maxLength={3}
                  onChange={(e) =>
                    onChange(
                      segments.map((v, j) =>
                        i === j
                          ? { ...s, currency: e.target.value.toUpperCase() }
                          : v,
                      ),
                    )
                  }
                />
              </label>
            )}
            <label>
              Optional field
              <input
                type="checkbox"
                checked={s.fallback !== undefined}
                onChange={(e) => {
                  const t = { ...s };
                  if (e.target.checked) t.fallback = '';
                  else delete t.fallback;
                  onChange(segments.map((v, j) => (i === j ? t : v)));
                }}
              />
            </label>
            {s.fallback !== undefined && (
              <label>
                Fallback
                <input
                  value={s.fallback}
                  onChange={(e) =>
                    onChange(
                      segments.map((v, j) =>
                        i === j ? { ...s, fallback: e.target.value } : v,
                      ),
                    )
                  }
                />
              </label>
            )}
            <button
              aria-label={`Remove ${label} field`}
              onClick={() => onChange(segments.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          </div>
        ),
      )}
      <details className={styles.advanced}><summary>Add contact details (optional)</summary><div className={styles.compact}>
        <label>
          Insert field into {label.toLowerCase()}
          <select value={field} onChange={(e) => setField(e.target.value)}>
            <option value="">Choose field</option>
            {fields.map((f) => (
              <option key={`${f.source}:${f.id}`} value={`${f.source}:${f.id}`}>
                {f.source === 'context' ? 'Context' : 'Recipient'} → {f.label}
              </option>
            ))}
          </select>
        </label>
        <button
          disabled={!field}
          onClick={() => {
            const f = fields.find((f) => `${f.source}:${f.id}` === field);
            if (f)
              onChange([
                ...segments,
                {
                  kind: 'field',
                  source: f.source,
                  fieldId: f.id,
                  fieldType: f.type,
                  format:
                    f.type === 'date' || f.type === 'datetime'
                      ? 'date'
                      : f.type === 'currency'
                        ? 'currency'
                        : 'text',
                  ...(f.type === 'currency' ? { currency: 'USD' } : {}),
                },
                { kind: 'literal', text: '' },
              ]);
          }}
        >
          Insert field
        </button>
        <button
          onClick={() => onChange([...segments, { kind: 'literal', text: '' }])}
        >
          Add text
        </button>
      </div></details>
    </section>
  );
}
function NoteSelectors({
  message,
  onChange,
}: {
  message: Extract<MessageSpec, { mode: 'guidance' }>;
  onChange: (m: MessageSpec) => void;
}) {
  const [selected, setSelected] = useState('');
  const notes = useQuery({
    queryKey: [...emailKey(), 'note-options'],
    queryFn: () => api.listRecords('notes', { limit: 50 }),
  });
  const chosen = [
    ...message.requiredContext,
    ...message.optionalContext,
  ].filter((s) => s.kind === 'note');
  return (
    <section>
      <h3>Selected related notes</h3>
      {chosen.map(
        (s) =>
          'recordId' in s && (
            <p key={s.recordId}>
              {notes.data?.records.find((n) => n.id === s.recordId)
                ?.displayName ?? 'Selected note'}{' '}
              · {message.requiredContext.includes(s) ? 'Required' : 'Optional'}{' '}
              <button
                onClick={() =>
                  onChange({
                    ...message,
                    requiredContext: message.requiredContext.filter(
                      (c) => c !== s,
                    ),
                    optionalContext: message.optionalContext.filter(
                      (c) => c !== s,
                    ),
                  })
                }
              >
                Remove note
              </button>
            </p>
          ),
      )}
      <label>
        Choose note
        <select value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Choose a note</option>
          {notes.data?.records.map((n) => (
            <option key={n.id} value={n.id}>
              {n.displayName}
            </option>
          ))}
        </select>
      </label>
      <button
        disabled={!selected}
        onClick={() => {
          onChange({
            ...message,
            requiredContext: [
              ...message.requiredContext,
              { kind: 'note', recordId: selected },
            ],
          });
          setSelected('');
        }}
      >
        Add required note
      </button>
      <button
        disabled={!selected}
        onClick={() => {
          onChange({
            ...message,
            optionalContext: [
              ...message.optionalContext,
              { kind: 'note', recordId: selected },
            ],
          });
          setSelected('');
        }}
      >
        Add optional note
      </button>
      <p className={styles.hint}>
        A selected note must be explicitly related to the context or recipient.
        No matching by title or guesswork.
      </p>
    </section>
  );
}
function RecordPicker({
  object,
  value,
  onChange,
}: {
  object?: ObjectDef | undefined;
  value: string;
  onChange: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const records = useInfiniteQuery({
    queryKey: [...emailKey(), 'context-options', object?.id],
    queryFn: ({ pageParam }) =>
      api.listRecords(object!.namePlural, { limit: 50, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (p, pages) =>
      p.records.length === 50 ? pages.length * 50 : undefined,
    enabled: !!object,
  });
  return (
    <>
      <label>
        Find context record
        <input value={search} onChange={(e) => setSearch(e.target.value)} />
      </label>
      <label>
        Context record
        <select
          aria-label="Context record"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose record</option>
          {records.data?.pages
            .flatMap((p) => p.records)
            .filter(
              (r) =>
                r.id === value ||
                r.displayName.toLowerCase().includes(search.toLowerCase()),
            )
            .map((r) => (
              <option key={r.id} value={r.id}>
                {r.displayName}
              </option>
            ))}
        </select>
      </label>
      {records.hasNextPage && (
        <button onClick={() => void records.fetchNextPage()}>
          Load more records
        </button>
      )}
      {records.error && <p role="alert">{records.error.message}</p>}
    </>
  );
}
function ScalarInput({
  field,
  value,
  onChange,
}: {
  field?: Field | undefined;
  value: unknown;
  onChange: (v: string | number | boolean) => void;
}) {
  return field?.type === 'boolean' ? (
    <select
      aria-label="Value"
      value={String(value ?? false)}
      onChange={(e) => onChange(e.target.value === 'true')}
    >
      <option value="true">True</option>
      <option value="false">False</option>
    </select>
  ) : field?.type === 'select' ? (
    <select
      aria-label="Value"
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Choose value</option>
      {(Array.isArray(field.options?.choices) ? field.options.choices : []).map(
        (c: unknown) => {
          const v = c as { value: string; label: string };
          return (
            <option key={v.value} value={v.value}>
              {v.label ?? v.value}
            </option>
          );
        },
      )}
    </select>
  ) : (
    <input
      aria-label="Value"
      type={
        ['number', 'rating'].includes(field?.type ?? '')
          ? 'number'
          : field?.type === 'date'
            ? 'date'
            : 'text'
      }
      value={String(value ?? '')}
      onChange={(e) =>
        onChange(
          ['number', 'rating'].includes(field?.type ?? '')
            ? Number(e.target.value)
            : e.target.value,
        )
      }
    />
  );
}
const scalarFields = (fields: Field[]) =>
  fields.filter((f) =>
    [
      'text',
      'number',
      'boolean',
      'select',
      'date',
      'datetime',
      'rating',
    ].includes(f.type),
  );
function TriggerEditor({
  definition: d,
  fields,
  onChange,
}: {
  definition: AutomationDef;
  fields: Field[];
  onChange: (d: AutomationDef) => void;
}) {
  return (
    <section className={styles.panel}>
      <label>
        When should it start?
        <select
          value={d.trigger.kind}
          onChange={(e) =>
            onChange({
              ...d,
              trigger:
                e.target.value === 'field_changed'
                  ? {
                      kind: 'field_changed',
                      fieldId: scalarFields(fields)[0]?.id ?? '',
                      to: '',
                    }
                  : { kind: e.target.value as 'manual' | 'created' },
            })
          }
        >
          <option value="manual">When I choose contacts to start with</option>
          <option value="created">When a new record is added</option>
          <option value="field_changed">When a record’s field changes</option>
        </select>
      </label>
      {d.trigger.kind === 'field_changed' && (
        <>
          <label>
            Field
            <select
              value={d.trigger.fieldId}
              onChange={(e) =>
                onChange({
                  ...d,
                  trigger: {
                    kind: 'field_changed',
                    fieldId: e.target.value,
                    to: '',
                  },
                })
              }
            >
              {scalarFields(fields).map((f) => (
                <option value={f.id} key={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Changes to
            <ScalarInput
              field={fields.find(
                (f) =>
                  d.trigger.kind === 'field_changed' &&
                  f.id === d.trigger.fieldId,
              )}
              value={d.trigger.to}
              onChange={(to) =>
                d.trigger.kind === 'field_changed' &&
                onChange({ ...d, trigger: { ...d.trigger, to } })
              }
            />
          </label>
          <label>
            Require a specific previous value
            <input
              type="checkbox"
              checked={Object.hasOwn(d.trigger, 'from')}
              onChange={(e) => {
                if (d.trigger.kind === 'field_changed') {
                  const trigger = { ...d.trigger };
                  if (e.target.checked) trigger.from = '';
                  else delete trigger.from;
                  onChange({ ...d, trigger });
                }
              }}
            />
          </label>
          {Object.hasOwn(d.trigger, 'from') && (
            <label>
              Changes from
              <ScalarInput
                field={fields.find(
                  (f) =>
                    d.trigger.kind === 'field_changed' &&
                    f.id === d.trigger.fieldId,
                )}
                value={d.trigger.from}
                onChange={(from) =>
                  d.trigger.kind === 'field_changed' &&
                  onChange({ ...d, trigger: { ...d.trigger, from } })
                }
              />
            </label>
          )}
        </>
      )}
      <p className={styles.hint}>
        {d.trigger.kind === 'manual'
          ? 'You stay in control of who starts. After activating this sequence, choose the contacts you want to include.'
          : d.trigger.kind === 'created'
            ? 'After activation, new records of this type can start the sequence. Existing records are not added.'
            : 'After activation, a change to this value can start the sequence. Records that already have this value are not added.'}
      </p>
    </section>
  );
}
function ConditionEditor({
  title,
  value,
  fields,
  onChange,
}: {
  title: string;
  value: Condition[];
  fields: Field[];
  onChange: (c: Condition[]) => void;
}) {
  const options = scalarFields(fields);
  return (
    <section>
      <h3>{title}</h3>
      {value.map((c, i) => (
        <div className={styles.token} key={i}>
          <select
            aria-label="Condition field"
            value={c.fieldId}
            onChange={(e) =>
              onChange(
                value.map((v, j) =>
                  i === j ? { ...c, fieldId: e.target.value } : v,
                ),
              )
            }
          >
            {options.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Comparison"
            value={c.operator}
            onChange={(e) =>
              onChange(
                value.map((v, j) =>
                  i === j
                    ? {
                        ...c,
                        operator: e.target.value as Condition['operator'],
                      }
                    : v,
                ),
              )
            }
          >
            {['equals', 'not_equals', 'is_set', 'is_not_set'].map((o) => (
              <option key={o} value={o}>
                {friendly(o)}
              </option>
            ))}
          </select>
          {['equals', 'not_equals'].includes(c.operator) && (
            <ScalarInput
              field={fields.find((f) => f.id === c.fieldId)}
              value={c.value}
              onChange={(val) =>
                onChange(
                  value.map((v, j) => (i === j ? { ...c, value: val } : v)),
                )
              }
            />
          )}
          <button onClick={() => onChange(value.filter((_, j) => i !== j))}>
            Remove condition
          </button>
        </div>
      ))}
      <button
        disabled={!options.length}
        onClick={() =>
          onChange([
            ...value,
            { fieldId: options[0]!.id, operator: 'equals', value: '' },
          ])
        }
      >
        Add condition
      </button>
    </section>
  );
}
function DelayEditor({
  value,
  onChange,
}: {
  value: { amount: number; unit: 'hours' | 'days' | 'business_days' };
  onChange: (v: typeof value) => void;
}) {
  return (
    <div className={styles.compact}>
      <label>
        Wait after previous send
        <input
          type="number"
          min={1}
          max={value.unit === 'hours' ? 8760 : 365}
          value={value.amount}
          onChange={(e) =>
            onChange({ ...value, amount: Number(e.target.value) })
          }
        />
      </label>
      <label>
        Delay unit
        <select
          value={value.unit}
          onChange={(e) =>
            onChange({ ...value, unit: e.target.value as typeof value.unit })
          }
        >
          <option value="hours">Elapsed hours</option>
          <option value="days">Calendar days</option>
          <option value="business_days">Business days</option>
        </select>
      </label>
    </div>
  );
}
function ManualEnrollment({
  automation,
  object,
}: {
  automation: EmailResource<AutomationDef>;
  object?: ObjectDef | undefined;
}) {
  const [selected, setSelected] = useState<string[]>([]),
    [recordId, setRecordId] = useState(''),
    [preview, setPreview] = useState<EnrollmentPreview>();
  const client = useQueryClient();
  const inspect = useMutation({
    mutationFn: () => resourceApi.enrollmentPreview(automation.id, selected),
    onSuccess: setPreview,
  });
  const start = useMutation({
    mutationFn: () =>
      resourceApi.enroll(automation.id, preview!.id, crypto.randomUUID()),
    onSuccess: () => {
      setPreview(undefined);
      setSelected([]);
      void client.invalidateQueries({ queryKey: emailKey() });
    },
  });
  return (
    <section className={styles.panel}>
      <h2>Start with existing records</h2>
      <p>
        Choose up to 50 records. Preview fixes the exact IDs and recipients for
        15 minutes. Every prepared email requires approval.
      </p>
      <RecordPicker object={object} value={recordId} onChange={setRecordId} />
      <button
        disabled={
          !recordId || selected.length >= 50 || selected.includes(recordId)
        }
        onClick={() => {
          setSelected([...selected, recordId]);
          setPreview(undefined);
        }}
      >
        Add selected record
      </button>
      <div className={styles.selection}>
        {selected.map((id) => (
          <p key={id}>
            {id}{' '}
            <button
              onClick={() => {
                setSelected(selected.filter((s) => s !== id));
                setPreview(undefined);
              }}
            >
              Remove
            </button>
          </p>
        ))}
      </div>
      <button
        disabled={!selected.length || inspect.isPending}
        onClick={() => inspect.mutate()}
      >
        Preview selection
      </button>
      {preview && (
        <>
          <p>Expires {new Date(preview.expires_at).toLocaleString()}</p>
          {preview.selection.map((s) => (
            <p key={s.recordId}>
              {s.recordId} → {s.address ?? 'Needs input'}{' '}
              {s.excluded ?? s.blocked}
            </p>
          ))}
          <button
            disabled={automation.state !== 'active' || start.isPending}
            onClick={() => start.mutate()}
          >
            Start this fixed selection
          </button>
        </>
      )}
      {[inspect.error, start.error].filter(Boolean).map((e, i) => (
        <p role="alert" key={i}>
          {e!.message}
        </p>
      ))}
      {start.isSuccess && (
        <p role="status">Runs created. Open progress below.</p>
      )}
    </section>
  );
}
function RunList({ id }: { id: string }) {
  const query = useInfiniteQuery({
    queryKey: [...emailKey(), 'runs', id],
    queryFn: ({ pageParam }) => resourceApi.runs(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
    refetchInterval: 5000,
  });
  return (
    <section>
      <h2>Run progress</h2>
      {query.data?.pages
        .flatMap((p) => p.items)
        .map((e) => (
          <p key={e.id}>
            <a href={`/app/email/enrollments/${e.id}`}>
              {e.address ?? 'Recipient needs input'}
            </a>{' '}
            · {friendly(e.state)} {e.reason ? `· ${friendly(e.reason)}` : ''}
            {e.due_at ? ` · ${new Date(e.due_at).toLocaleString()}` : ''}
          </p>
        ))}
      {query.hasNextPage && (
        <button onClick={() => void query.fetchNextPage()}>
          Load more runs
        </button>
      )}
      {query.error && <p role="alert">{query.error.message}</p>}
    </section>
  );
}
function RunView({ id }: { id: string }) {
  const query = useQuery({
    queryKey: [...emailKey(), 'run', id],
    queryFn: () => resourceApi.run(id),
    refetchInterval: 5000,
  });
  const client = useQueryClient();
  usePageHelpDetails({
    view: 'automation-run',
    enrollmentId: id,
    ...(query.data ? {
      automationId: query.data.automation_id,
      name: query.data.name,
      state: query.data.state,
      reason: query.data.reason,
      explanation: query.data.explanation,
    } : {}),
  });
  const action = useMutation({
    mutationFn: (command: string) =>
      resourceApi.runCommand(id, command, query.data!.revision),
    onSuccess: () => {
      void query.refetch();
      void client.invalidateQueries({ queryKey: emailKey() });
    },
  });
  if (!query.data) return <p>{query.error?.message ?? 'Loading run…'}</p>;
  const run = query.data;
  return (
    <section>
      <a href={url('automations', run.automation_id)}>← {run.name}</a>
      <h2>{run.address ?? 'Recipient needs input'}</h2>
      <p role="status">
        {friendly(run.state)} ·{' '}
        {friendly(
          run.reason ??
            (run.state === 'awaiting_review' ? 'Waiting for you to send' : ''),
        )}
      </p>
      <p>{run.explanation}</p>
      {run.due_at && (
        <p>
          Next due: {new Date(run.due_at).toLocaleString()} (
          {run.definition.timezone})
        </p>
      )}
      <p>
        Context record: {run.context_name??'Record unavailable'} · Current step{' '}
        {Math.min(run.step_index + 1, run.definition.steps.length)} of{' '}
        {run.definition.steps.length}
      </p>
      <div className={styles.toolbar}>
        {['stopped', 'completed'].includes(run.state) ? (
          <details>
            <summary>Restart after reviewing prior history</summary>
            <p>
              Creates a new run. Resolve any do-not-contact condition first.
              Prior sends are retained.
            </p>
            <button
              disabled={action.isPending}
              onClick={() => action.mutate('restart')}
            >
              Restart
            </button>
          </details>
        ) : (
          <>
            <button
              disabled={action.isPending}
              onClick={() =>
                action.mutate(
                  run.state === 'paused' || run.state === 'blocked'
                    ? 'resume'
                    : 'pause',
                )
              }
            >
              {run.state === 'paused' || run.state === 'blocked'
                ? 'Revalidate and resume'
                : 'Pause'}
            </button>
            <button
              disabled={action.isPending}
              onClick={() => action.mutate('stop')}
            >
              Stop this sequence
            </button>
          </>
        )}
      </div>
      {action.error && <p role="alert">{action.error.message}</p>}
      {run.steps.map((s,index) => (
        <article key={s.id} className={styles.row}>
          <p>
            {index+1}. Message · {friendly(s.state)}{' '}
            {s.accepted_at
              ? `· Gmail accepted ${new Date(s.accepted_at).toLocaleString()}`
              : ''}
          </p>
          {s.generation?.result && (
            <div>
              <p>{s.generation.result.reason}</p>
              {s.generation.result.questions?.map((q, i) => (
                <p key={i}>{q}</p>
              ))}
            </div>
          )}
          {s.thread_id && (
            <a href={`/app/brief?emailThreadId=${s.thread_id}`}>
              Open email review
            </a>
          )}
        </article>
      ))}
      {run.reason === 'send_time_required' && (
        <ConfirmSendTime
          attemptId={
            run.steps.find((s) => s.send_attempt_id && !s.accepted_at)
              ?.send_attempt_id ?? ''
          }
          onSaved={() => void query.refetch()}
        />
      )}
      <h3>History</h3>
      {run.events.map((e) => (
        <p key={e.id}>
          {new Date(e.created_at).toLocaleString()} · {friendly(e.kind)}
        </p>
      ))}
    </section>
  );
}
export function RecordEmailActions({ recordId }: { recordId: string }) {
  const enabled = useQuery({
    queryKey: [...emailKey(), 'capabilities'],
    queryFn: resourceApi.capabilities,
    retry: false,
  });
  const runs = useQuery({
    queryKey: [...emailKey(), 'record-runs', recordId],
    queryFn: () => resourceApi.recordRuns(recordId),
    enabled: enabled.data?.enabled === true,
  });
  if (!enabled.data?.enabled) return null;
  return (
    <section>
      <p>
        <a href={`${url('templates')}?recordId=${recordId}`}>Use template</a> ·{' '}
        <a href={`${url('automations')}?recordId=${recordId}`}>
          Start sequence
        </a>
      </p>
      {runs.data?.items.map((r) => (
        <p key={r.id}>
          <a href={`/app/email/enrollments/${r.id}`}>
            {r.name}: {friendly(r.state)}
          </a>{' '}
          {r.reason ? `· ${friendly(r.reason)}` : ''}
        </p>
      ))}
    </section>
  );
}

function ConfirmSendTime({
  attemptId,
  onSaved,
}: {
  attemptId: string;
  onSaved: () => void;
}) {
  const [time, setTime] = useState('');
  const confirm = useMutation({
    mutationFn: () =>
      resourceApi.confirmTime(attemptId, new Date(time).toISOString()),
    onSuccess: onSaved,
  });
  return (
    <section>
      <label>
        Send time confirmed in Gmail
        <input
          type="datetime-local"
          value={time}
          onChange={(e) => setTime(e.target.value)}
        />
      </label>
      <button
        disabled={!time || !attemptId || confirm.isPending}
        onClick={() => confirm.mutate()}
      >
        Use this send time
      </button>
      <p>Threaded follow-ups also wait for the provider thread receipt.</p>
      {confirm.error && <p role="alert">{confirm.error.message}</p>}
    </section>
  );
}
